import { Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useState, forwardRef } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { DutyInput, FriDefaults, FatigueSavePayload } from '@/types/fatigue';
import { calculateFriSchedule, DEFAULT_FRI_DEFAULTS, formatHoursMinutes, internalMath, sortDutyInputs } from '@/lib/fatigueCalculations';

const workloadOptions = [
  { value: '3', label: 'Extremely demanding' },
  { value: '2', label: 'Moderately demanding' },
  { value: '1', label: 'Moderately undemanding' },
  { value: '0', label: 'Extremely undemanding' },
];

const attentionOptions = [
  { value: '3', label: 'All/nearly all the time' },
  { value: '2', label: 'Most of the time' },
  { value: '1', label: 'Some of the time' },
  { value: '0', label: 'Rarely/none of the time' },
];

const createDuty = (defaults: FriDefaults = DEFAULT_FRI_DEFAULTS): DutyInput => ({
  id: crypto.randomUUID(),
  day: '',
  onDuty: '',
  offDuty: '',
  useDefaultCommute: true,
  useDefaultJobBreaks: true,
  commuteMinutes: defaults.commuteMinutes,
  jobWorkload: defaults.jobWorkload,
  jobAttention: defaults.jobAttention,
  breakFrequencyMinutes: defaults.breakFrequencyMinutes,
  breakAverageLengthMinutes: defaults.breakAverageLengthMinutes,
  continuousWorkLengthMinutes: defaults.continuousWorkLengthMinutes,
  breakAfterContinuousMinutes: defaults.breakAfterContinuousMinutes,
});

const parseImportRows = (text: string) => {
  const rawLines = text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);
  if (!rawLines.length) return [];

  const lines = [...rawLines];
  const sample = lines[0].toLowerCase();
  if (sample.includes('date') || sample.includes('day')) {
    lines.shift();
  }

  return lines.map(line => {
    const delimiter = line.includes('\t') ? '\t' : line.includes(',') ? ',' : ';';
    const parts = line.split(delimiter).map(part => part.trim());
    return {
      day: parts[0] ?? '',
      onDuty: parts[1] ?? '',
      offDuty: parts[2] ?? '',
      commuteMinutes: parts[3] ? Number(parts[3]) : undefined,
    };
  }).filter(row => row.day && row.onDuty && row.offDuty);
};

const normalizeDateForInput = (value: string) => {
  const raw = value.trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(raw)) {
    const [dayRaw, monthRaw, yearRaw] = raw.split('/');
    let year = Number(yearRaw);
    const month = Number(monthRaw);
    const day = Number(dayRaw);
    if (year < 100) year += year < 50 ? 2000 : 1900;
    if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
      return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    }
  }
  if (/^\d{8}$/.test(raw)) {
    const yearFirst = Number(raw.slice(0, 4));
    if (yearFirst > 1900) {
      return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
    }
    return `${raw.slice(4, 8)}-${raw.slice(2, 4)}-${raw.slice(0, 2)}`;
  }
  const numeric = Number(raw);
  if (Number.isFinite(numeric) && numeric > 20000) {
    const excelEpoch = Date.UTC(1899, 11, 30);
    const date = new Date(excelEpoch + numeric * 24 * 60 * 60 * 1000);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  }
  return raw;
};

const createDutyFromRow = (
  row: { day: string; onDuty: string; offDuty: string; commuteMinutes?: number },
  defaults: FriDefaults = DEFAULT_FRI_DEFAULTS
): DutyInput => ({
  ...createDuty(defaults),
  day: normalizeDateForInput(row.day),
  onDuty: row.onDuty,
  offDuty: row.offDuty,
  commuteMinutes: Number.isFinite(row.commuteMinutes) ? row.commuteMinutes : defaults.commuteMinutes,
  useDefaultCommute: !Number.isFinite(row.commuteMinutes),
});

const escapeCsvValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  if (/["\n,]/.test(raw)) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
};

const buildPolicyContext = (
  duties: DutyInput[],
  defaults: FriDefaults,
  fatigueCalc: ReturnType<typeof calculateFriSchedule>,
  riskCalc: ReturnType<typeof calculateFriSchedule>
) => {
  const rows = duties.map((duty, index) => {
    const dayIndex = internalMath.parseDayToIndex(duty.day);
    const startMinutes = internalMath.parseTimeToMinutes(duty.onDuty);
    const endMinutes = internalMath.parseTimeToMinutes(duty.offDuty);
    if (dayIndex === null || startMinutes === null || endMinutes === null) {
      return null;
    }
    const startTime = dayIndex + startMinutes / 1440;
    let endTime = dayIndex + endMinutes / 1440;
    if (endTime < startTime) endTime += 1;
    const dutyHours = (endTime - startTime) * 24;
    const commuteMinutes = duty.commuteMinutes ?? defaults.commuteMinutes;
    const totalHours = dutyHours + commuteMinutes / 60;
    return {
      id: duty.id,
      row: index + 1,
      date: duty.day,
      time: `${duty.onDuty || '--:--'}-${duty.offDuty || '--:--'}`,
      startTime,
      totalHours,
    };
  }).filter(Boolean) as Array<{
    id: string;
    row: number;
    date: string;
    time: string;
    startTime: number;
    totalHours: number;
  }>;

  const sorted = [...rows].sort((a, b) => a.startTime - b.startTime);
  const rolling7 = new Map<string, number>();
  const rolling14 = new Map<string, number>();
  let max7 = 0;
  let max14 = 0;

  let startIndex = 0;
  let total = 0;
  for (let i = 0; i < sorted.length; i += 1) {
    const current = sorted[i];
    while (current.startTime - sorted[startIndex].startTime > 7) {
      total -= sorted[startIndex].totalHours;
      startIndex += 1;
    }
    total += current.totalHours;
    rolling7.set(current.id, total);
    if (total > max7) max7 = total;
  }

  startIndex = 0;
  let count = 0;
  for (let i = 0; i < sorted.length; i += 1) {
    const current = sorted[i];
    while (current.startTime - sorted[startIndex].startTime > 13) {
      count -= 1;
      startIndex += 1;
    }
    count += 1;
    rolling14.set(current.id, count);
    if (count > max14) max14 = count;
  }

  const fatigueById = new Map<string, number>();
  const riskById = new Map<string, number>();
  duties.forEach((duty, index) => {
    if (fatigueCalc.duties[index]) {
      fatigueById.set(duty.id, fatigueCalc.duties[index].index);
    }
    if (riskCalc.duties[index]) {
      riskById.set(duty.id, riskCalc.duties[index].index);
    }
  });

  const maxFatigue = Math.max(0, ...fatigueCalc.duties.map(item => item.index));
  const maxRisk = Math.max(0, ...riskCalc.duties.map(item => item.index));

  return {
    rowsById: new Map(rows.map(row => [row.id, row])),
    rolling7,
    rolling14,
    max7,
    max14,
    fatigueById,
    riskById,
    maxFatigue,
    maxRisk,
  };
};

const parseIsoDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export interface ShiftTableHandle {
  getSavePayload: () => FatigueSavePayload;
  loadSavePayload: (payload: FatigueSavePayload) => void;
}

interface ShiftTableProps {
  onSnapshotChange?: (payload: FatigueSavePayload) => void;
}

const ShiftTable = forwardRef<ShiftTableHandle, ShiftTableProps>(({ onSnapshotChange }, ref) => {
  const [defaults, setDefaults] = useState<FriDefaults>(DEFAULT_FRI_DEFAULTS);
  const [rotating, setRotating] = useState(false);
  const [useDefaultsOnly, setUseDefaultsOnly] = useState(true);
  const [duties, setDuties] = useState<DutyInput[]>([]);
  const [fatigueCalc, setFatigueCalc] = useState(() => calculateFriSchedule([], DEFAULT_FRI_DEFAULTS, 'fatigue'));
  const [riskCalc, setRiskCalc] = useState(() => calculateFriSchedule([], DEFAULT_FRI_DEFAULTS, 'risk'));
  const [importText, setImportText] = useState('');
  const [importMode, setImportMode] = useState<'append' | 'replace'>('append');
  const [selectedPolicyDate, setSelectedPolicyDate] = useState<Date | undefined>(undefined);
  const savePayload = useMemo<FatigueSavePayload>(() => ({
    defaults,
    rotating,
    useDefaultsOnly,
    duties,
  }), [defaults, rotating, useDefaultsOnly, duties]);

  useEffect(() => {
    if (onSnapshotChange) {
      onSnapshotChange(savePayload);
    }
  }, [onSnapshotChange, savePayload]);

  const recalculate = useCallback((nextDuties: DutyInput[], nextDefaults: FriDefaults) => {
    const sorted = sortDutyInputs(nextDuties);
    const fatigue = calculateFriSchedule(sorted, nextDefaults, 'fatigue');
    const risk = calculateFriSchedule(sorted, nextDefaults, 'risk');
    setDuties(sorted);
    setFatigueCalc(fatigue);
    setRiskCalc(risk);
    return { sorted, fatigue, risk };
  }, []);

  const normalizeLoadedDuties = useCallback((items: DutyInput[], nextDefaults: FriDefaults, defaultsOnly: boolean) => {
    return items.map((duty) => {
      const merged = { ...createDuty(nextDefaults), ...duty };
      const hasCommute = Number.isFinite(merged.commuteMinutes);
      const inferredUseDefaultCommute = hasCommute
        ? merged.commuteMinutes === nextDefaults.commuteMinutes
        : true;
      const useDefaultCommute = typeof duty.useDefaultCommute === 'boolean'
        ? duty.useDefaultCommute
        : inferredUseDefaultCommute;
      const useDefaultJobBreaks = defaultsOnly
        ? true
        : typeof duty.useDefaultJobBreaks === 'boolean'
          ? duty.useDefaultJobBreaks
          : true;

      return {
        ...merged,
        id: merged.id || crypto.randomUUID(),
        useDefaultCommute,
        useDefaultJobBreaks,
        commuteMinutes: hasCommute ? merged.commuteMinutes : nextDefaults.commuteMinutes,
        jobWorkload: Number.isFinite(merged.jobWorkload) ? merged.jobWorkload : nextDefaults.jobWorkload,
        jobAttention: Number.isFinite(merged.jobAttention) ? merged.jobAttention : nextDefaults.jobAttention,
        breakFrequencyMinutes: Number.isFinite(merged.breakFrequencyMinutes)
          ? merged.breakFrequencyMinutes
          : nextDefaults.breakFrequencyMinutes,
        breakAverageLengthMinutes: Number.isFinite(merged.breakAverageLengthMinutes)
          ? merged.breakAverageLengthMinutes
          : nextDefaults.breakAverageLengthMinutes,
        continuousWorkLengthMinutes: Number.isFinite(merged.continuousWorkLengthMinutes)
          ? merged.continuousWorkLengthMinutes
          : nextDefaults.continuousWorkLengthMinutes,
        breakAfterContinuousMinutes: Number.isFinite(merged.breakAfterContinuousMinutes)
          ? merged.breakAfterContinuousMinutes
          : nextDefaults.breakAfterContinuousMinutes,
      };
    });
  }, []);

  useImperativeHandle(ref, () => ({
    getSavePayload: () => savePayload,
    loadSavePayload: (payload: FatigueSavePayload) => {
      const nextDefaults = payload?.defaults ?? DEFAULT_FRI_DEFAULTS;
      const nextRotating = payload?.rotating ?? false;
      const nextUseDefaultsOnly = payload?.useDefaultsOnly ?? true;
      const rawDuties = Array.isArray(payload?.duties) ? payload.duties : [];
      const nextDuties = normalizeLoadedDuties(rawDuties, nextDefaults, nextUseDefaultsOnly);

      setDefaults(nextDefaults);
      setRotating(nextRotating);
      setUseDefaultsOnly(nextUseDefaultsOnly);
      setImportText('');
      setSelectedPolicyDate(undefined);
      recalculate(nextDuties, nextDefaults);
    },
  }), [normalizeLoadedDuties, recalculate, savePayload]);

  const addDuty = () => setDuties(prev => [...prev, createDuty(defaults)]);
  const removeDuty = (id: string) => setDuties(prev => prev.filter(duty => duty.id !== id));

  const updateDuty = (id: string, field: keyof DutyInput, value: string | number | boolean) => {
    setDuties(prev => prev.map(duty => {
      if (duty.id !== id) return duty;
      if (field === 'commuteMinutes') {
        return { ...duty, commuteMinutes: value as number, useDefaultCommute: false };
      }
      return { ...duty, [field]: value };
    }));
  };

  const updateDefaults = (field: keyof FriDefaults, value: number) => {
    setDefaults(prev => ({ ...prev, [field]: value }));
  };

  const handleDefaultsOnlyToggle = (value: boolean) => {
    setUseDefaultsOnly(value);
    if (value) {
      setDuties(prev => prev.map(duty => ({
        ...duty,
        useDefaultJobBreaks: true,
      })));
    }
  };
  const handlePolicyDateSelect = (date?: Date) => {
    if (!date) {
      setSelectedPolicyDate(undefined);
      return;
    }
    setSelectedPolicyDate(prev => {
      if (prev && formatDateKey(prev) === formatDateKey(date)) {
        return undefined;
      }
      return date;
    });
  };

  const downloadFile = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => {
    const header = 'Date,On duty,Off duty,Total commute (min)';
    const sample = '2026-02-01,07:00,19:00,80';
    const csv = `${header}\n${sample}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadFile(blob, 'fatigue-schedule-template.csv');
  };

  const handleImport = () => {
    const rows = parseImportRows(importText);
    if (!rows.length) return;
    const mapped = rows.map(row => createDutyFromRow(row, defaults));
    setDuties(prev => (importMode === 'replace' ? mapped : [...prev, ...mapped]));
    setImportText('');
  };

  const handleFileImport = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const content = typeof reader.result === 'string' ? reader.result : '';
      setImportText(content);
    };
    reader.readAsText(file);
  };

  const handleCalculate = () => {
    recalculate(duties, defaults);
  };
  const handleClear = () => {
    setDuties([]);
    setFatigueCalc(calculateFriSchedule([], defaults, 'fatigue'));
    setRiskCalc(calculateFriSchedule([], defaults, 'risk'));
    setImportText('');
  };
  const buildReportState = () => {
    return recalculate(duties, defaults);
  };
  const handleExportCsv = () => {
    const { sorted, fatigue, risk } = buildReportState();
    if (!sorted.length || fatigue.errors.length > 0 || risk.errors.length > 0) return;
    const context = buildPolicyContext(sorted, defaults, fatigue, risk);
    const nowLabel = new Date().toLocaleString();

    const scheduleHeader = [
      'Row',
      'Date',
      'On duty',
      'Off duty',
      'Total commute (min)',
      'Duty length (hrs)',
      'Rest length (hrs)',
      'Fatigue index',
      'Risk index',
    ].map(escapeCsvValue).join(',');

    const scheduleRows = sorted.map((duty, index) => {
      const fatigueRow = fatigue.duties[index];
      const riskRow = risk.duties[index];
      const commute = Number.isFinite(duty.commuteMinutes) ? duty.commuteMinutes : defaults.commuteMinutes;
      const dutyHoursValue = fatigueRow?.dutyLengthHours;
      const restHoursValue = fatigueRow?.restLengthHours;
      const fatigueIndexValue = fatigueRow?.index;
      const riskIndexValue = riskRow?.index;
      const dutyHours = Number.isFinite(dutyHoursValue) ? dutyHoursValue.toFixed(2) : '';
      const restHours = fatigueRow
        ? (restHoursValue === null ? 'Fully rested' : Number.isFinite(restHoursValue) ? restHoursValue.toFixed(2) : '')
        : '';
      const fatigueIndex = Number.isFinite(fatigueIndexValue) ? fatigueIndexValue.toFixed(1) : '';
      const riskIndex = Number.isFinite(riskIndexValue) ? riskIndexValue.toFixed(2) : '';
      const values = [
        index + 1,
        duty.day,
        duty.onDuty,
        duty.offDuty,
        commute,
        dutyHours,
        restHours,
        fatigueIndex,
        riskIndex,
      ];
      return values.map(escapeCsvValue).join(',');
    });

    const orderedFindings = [...fatigue.policyFindings].sort((a, b) => {
      const aRow = a.dutyId ? context.rowsById.get(a.dutyId) : null;
      const bRow = b.dutyId ? context.rowsById.get(b.dutyId) : null;
      const aTime = aRow?.startTime ?? Number.POSITIVE_INFINITY;
      const bTime = bRow?.startTime ?? Number.POSITIVE_INFINITY;
      if (aTime !== bTime) return aTime - bTime;
      return a.id.localeCompare(b.id);
    });

    const findingsHeader = [
      'Date',
      'Row',
      'Time',
      'Fatigue',
      'Risk',
      'Cumulative hours (7d)',
      'Cumulative days (14d)',
      'Severity',
      'Rule',
      'Message',
    ].map(escapeCsvValue).join(',');

    const findingsRows = orderedFindings.map(finding => {
      const row = finding.dutyId ? context.rowsById.get(finding.dutyId) : null;
      const fatigueValue = finding.dutyId ? context.fatigueById.get(finding.dutyId) : context.maxFatigue;
      const riskValue = finding.dutyId ? context.riskById.get(finding.dutyId) : context.maxRisk;
      const hoursValue = finding.dutyId ? context.rolling7.get(finding.dutyId) : context.max7;
      const daysValue = finding.dutyId ? context.rolling14.get(finding.dutyId) : context.max14;
      const values = [
        row?.date ?? 'Schedule-wide',
        row?.row ?? '',
        row?.time ?? '',
        Number.isFinite(fatigueValue) ? fatigueValue.toFixed(1) : '',
        Number.isFinite(riskValue) ? riskValue.toFixed(2) : '',
        Number.isFinite(hoursValue) ? hoursValue.toFixed(1) : '',
        Number.isFinite(daysValue) ? Math.round(daysValue).toString() : '',
        finding.severity === 'breach' ? 'Breach' : 'Warning',
        finding.rule,
        finding.message,
      ];
      return values.map(escapeCsvValue).join(',');
    });

    const lines = [
      ['Report generated', nowLabel].map(escapeCsvValue).join(','),
      ['Disclaimer', 'Beta: verify calculations against the HSE FRI and local policy before use.'].map(escapeCsvValue).join(','),
      '',
      'Schedule',
      scheduleHeader,
      ...scheduleRows,
      '',
      'Policy findings',
      findingsHeader,
      ...findingsRows,
    ];

    const csv = `${lines.join('\n')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadFile(blob, 'fatigue-report.csv');
  };

  const handleExportPdf = () => {
    const { sorted, fatigue, risk } = buildReportState();
    if (!sorted.length || fatigue.errors.length > 0 || risk.errors.length > 0) return;
    const context = buildPolicyContext(sorted, defaults, fatigue, risk);
    const nowLabel = new Date().toLocaleString();

    const scheduleBody = sorted.map((duty, index) => {
      const fatigueRow = fatigue.duties[index];
      const riskRow = risk.duties[index];
      const commute = Number.isFinite(duty.commuteMinutes) ? duty.commuteMinutes : defaults.commuteMinutes;
      const dutyHoursValue = fatigueRow?.dutyLengthHours;
      const restHoursValue = fatigueRow?.restLengthHours;
      const fatigueIndexValue = fatigueRow?.index;
      const riskIndexValue = riskRow?.index;
      const dutyHours = Number.isFinite(dutyHoursValue) ? dutyHoursValue.toFixed(2) : '';
      const restHours = fatigueRow
        ? (restHoursValue === null ? 'Fully rested' : Number.isFinite(restHoursValue) ? restHoursValue.toFixed(2) : '')
        : '';
      const fatigueIndex = Number.isFinite(fatigueIndexValue) ? fatigueIndexValue.toFixed(1) : '';
      const riskIndex = Number.isFinite(riskIndexValue) ? riskIndexValue.toFixed(2) : '';
      return [
        String(index + 1),
        duty.day,
        duty.onDuty,
        duty.offDuty,
        String(commute),
        dutyHours,
        restHours,
        fatigueIndex,
        riskIndex,
      ];
    });

    const orderedFindings = [...fatigue.policyFindings].sort((a, b) => {
      const aRow = a.dutyId ? context.rowsById.get(a.dutyId) : null;
      const bRow = b.dutyId ? context.rowsById.get(b.dutyId) : null;
      const aTime = aRow?.startTime ?? Number.POSITIVE_INFINITY;
      const bTime = bRow?.startTime ?? Number.POSITIVE_INFINITY;
      if (aTime !== bTime) return aTime - bTime;
      return a.id.localeCompare(b.id);
    });

    const findingsBody = orderedFindings.map(finding => {
      const row = finding.dutyId ? context.rowsById.get(finding.dutyId) : null;
      const fatigueValue = finding.dutyId ? context.fatigueById.get(finding.dutyId) : context.maxFatigue;
      const riskValue = finding.dutyId ? context.riskById.get(finding.dutyId) : context.maxRisk;
      const hoursValue = finding.dutyId ? context.rolling7.get(finding.dutyId) : context.max7;
      const daysValue = finding.dutyId ? context.rolling14.get(finding.dutyId) : context.max14;
      return [
        row?.date ?? 'Schedule-wide',
        row?.row ? String(row.row) : '',
        row?.time ?? '',
        Number.isFinite(fatigueValue) ? fatigueValue.toFixed(1) : '',
        Number.isFinite(riskValue) ? riskValue.toFixed(2) : '',
        Number.isFinite(hoursValue) ? hoursValue.toFixed(1) : '',
        Number.isFinite(daysValue) ? Math.round(daysValue).toString() : '',
        finding.severity === 'breach' ? 'Breach' : 'Warning',
        finding.rule,
        finding.message,
      ];
    });

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    doc.setFontSize(14);
    doc.text('Fatigue report', 40, 40);
    doc.setFontSize(10);
    doc.text(`Generated ${nowLabel}`, 40, 58);
    doc.text('Disclaimer: Beta - verify calculations against the HSE FRI and local policy before use.', 40, 72);

    autoTable(doc, {
      head: [[
        'Row',
        'Date',
        'On duty',
        'Off duty',
        'Commute (min)',
        'Duty (hrs)',
        'Rest (hrs)',
        'Fatigue',
        'Risk',
      ]],
      body: scheduleBody,
      startY: 92,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [30, 41, 59] },
    });

    const nextY = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 92;
    const tableStart = nextY + 20;

    autoTable(doc, {
      head: [[
        'Date',
        'Row',
        'Time',
        'Fatigue',
        'Risk',
        'Hours (7d)',
        'Days (14d)',
        'Severity',
        'Rule',
        'Message',
      ]],
      body: findingsBody,
      startY: tableStart,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [30, 41, 59] },
      columnStyles: {
        9: { cellWidth: 240 },
      },
    });

    doc.save('fatigue-report.pdf');
  };

  const policyFindings = fatigueCalc.policyFindings;
  const errors = fatigueCalc.errors;
  const policyContext = useMemo(
    () => buildPolicyContext(duties, defaults, fatigueCalc, riskCalc),
    [duties, defaults, fatigueCalc, riskCalc]
  );

  const summaryBadge = useMemo(() => {
    if (!policyFindings.length) return null;
    const breaches = policyFindings.filter(item => item.severity === 'breach').length;
    if (breaches > 0) {
      return <Badge variant="destructive">{breaches} policy breach{breaches === 1 ? '' : 'es'}</Badge>;
    }
    return <Badge variant="secondary">Policy warnings</Badge>;
  }, [policyFindings]);
  const policyGroups = useMemo(() => {
    const ordered = [...policyFindings].sort((a, b) => {
      const aRow = a.dutyId ? policyContext.rowsById.get(a.dutyId) : null;
      const bRow = b.dutyId ? policyContext.rowsById.get(b.dutyId) : null;
      const aTime = aRow?.startTime ?? Number.POSITIVE_INFINITY;
      const bTime = bRow?.startTime ?? Number.POSITIVE_INFINITY;
      if (aTime !== bTime) return aTime - bTime;
      return a.id.localeCompare(b.id);
    });

    const groups: Array<{ label: string; items: typeof policyFindings }> = [];
    ordered.forEach(finding => {
      const row = finding.dutyId ? policyContext.rowsById.get(finding.dutyId) : null;
      const rawLabel = row?.date?.trim();
      const label = row ? (rawLabel || 'Unknown date') : 'Schedule-wide';
      const last = groups[groups.length - 1];
      if (!last || last.label !== label) {
        groups.push({ label, items: [finding] });
      } else {
        last.items.push(finding);
      }
    });
    return groups;
  }, [policyFindings, policyContext]);
  const exportErrors = useMemo(() => {
    if (!duties.length) return [];
    return calculateFriSchedule(duties, defaults, 'fatigue').errors;
  }, [duties, defaults]);
  const selectedPolicyKey = useMemo(
    () => (selectedPolicyDate ? formatDateKey(selectedPolicyDate) : null),
    [selectedPolicyDate]
  );
  const policyCalendar = useMemo(() => {
    const dates = new Map<string, { severity: 'warning' | 'breach'; count: number }>();
    policyFindings.forEach(finding => {
      if (!finding.dutyId) return;
      const row = policyContext.rowsById.get(finding.dutyId);
      if (!row?.date) return;
      const key = row.date;
      const existing = dates.get(key);
      const severity = finding.severity === 'breach' ? 'breach' : 'warning';
      if (!existing) {
        dates.set(key, { severity, count: 1 });
        return;
      }
      dates.set(key, {
        severity: existing.severity === 'breach' || severity === 'breach' ? 'breach' : 'warning',
        count: existing.count + 1,
      });
    });

    const breachDays: Date[] = [];
    const warningDays: Date[] = [];
    dates.forEach((value, key) => {
      const date = parseIsoDate(key);
      if (!date) return;
      if (value.severity === 'breach') {
        breachDays.push(date);
      } else {
        warningDays.push(date);
      }
    });

    const allDays = [...breachDays, ...warningDays].sort((a, b) => a.getTime() - b.getTime());
    const defaultMonth = allDays.length ? allDays[0] : new Date();

    return {
      breachDays,
      warningDays,
      defaultMonth,
      hasDates: allDays.length > 0,
      totalBreaches: breachDays.length,
      totalWarnings: warningDays.length,
    };
  }, [policyFindings, policyContext]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>Model Settings</CardTitle>
            <Badge variant="secondary">Beta</Badge>
          </div>
          <CardDescription>
            HSE/Network Rail fatigue model with policy checks. Beta: verify calculations against the HSE FRI and local policy before use.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-1">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Rotating pattern</p>
            <div className="flex items-center gap-2">
              <Switch checked={rotating} onCheckedChange={setRotating} />
              <span className="text-sm">{rotating ? 'Rotating schedule' : 'Non-rotating schedule'}</span>
            </div>
            {!rotating && (
              <p className="text-xs text-muted-foreground">
                The HSE FRI is intended for rotating shift patterns. Non-rotating schedules need additional judgement.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {summaryBadge}
            {errors.length > 0 && (
              <span className="text-xs text-destructive">Fix input errors before relying on results.</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Defaults</CardTitle>
          <CardDescription>Applied to duties marked Default. Commute is total (out + back).</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <label className="text-sm font-medium">Total commute (min)</label>
            <Input
              type="number"
              value={defaults.commuteMinutes}
              onChange={(event) => updateDefaults('commuteMinutes', parseInt(event.target.value, 10) || 0)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Workload</label>
            <Select
              value={String(defaults.jobWorkload)}
              onValueChange={(value) => updateDefaults('jobWorkload', parseInt(value, 10))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {workloadOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Attention</label>
            <Select
              value={String(defaults.jobAttention)}
              onValueChange={(value) => updateDefaults('jobAttention', parseInt(value, 10))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {attentionOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Break frequency (min)</label>
            <Input
              type="number"
              value={defaults.breakFrequencyMinutes}
              onChange={(event) => updateDefaults('breakFrequencyMinutes', parseInt(event.target.value, 10) || 0)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Break average length (min)</label>
            <Input
              type="number"
              value={defaults.breakAverageLengthMinutes}
              onChange={(event) => updateDefaults('breakAverageLengthMinutes', parseInt(event.target.value, 10) || 0)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Continuous work length (min)</label>
            <Input
              type="number"
              value={defaults.continuousWorkLengthMinutes}
              onChange={(event) => updateDefaults('continuousWorkLengthMinutes', parseInt(event.target.value, 10) || 0)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Break after continuous (min)</label>
            <Input
              type="number"
              value={defaults.breakAfterContinuousMinutes}
              onChange={(event) => updateDefaults('breakAfterContinuousMinutes', parseInt(event.target.value, 10) || 0)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <CardTitle>Schedule</CardTitle>
            <CardDescription>
              Use day number or date (YYYY-MM-DD). Times use 24-hour clock. Commute is total (out + back).
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleClear}>Clear</Button>
            <Button
              variant="outline"
              onClick={handleExportCsv}
              disabled={exportErrors.length > 0}
              title={exportErrors.length > 0 ? 'Fix input errors before exporting.' : 'Export CSV'}
            >
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={handleExportPdf}
              disabled={exportErrors.length > 0}
              title={exportErrors.length > 0 ? 'Fix input errors before exporting.' : 'Export PDF'}
            >
              Export PDF
            </Button>
            <Button size="lg" onClick={handleCalculate} className="px-6">Calculate schedule</Button>
          </div>
        </CardHeader>
        <CardContent>
          {exportErrors.length > 0 && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Fix inputs before exporting</AlertTitle>
              <AlertDescription>Exports are disabled until all input errors are resolved.</AlertDescription>
            </Alert>
          )}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              <Button onClick={addDuty}>Add duty</Button>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Switch checked={useDefaultsOnly} onCheckedChange={handleDefaultsOnlyToggle} />
              <span>Use default job/breaks for all duties</span>
            </div>
          </div>
          <div className="mb-6 rounded-lg border border-border/60 bg-muted/30 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".csv,.tsv,.txt"
                  onChange={(event) => handleFileImport(event.target.files?.[0] ?? null)}
                  className="max-w-xs"
                />
                <Select value={importMode} onValueChange={(value) => setImportMode(value as 'append' | 'replace')}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="append">Append</SelectItem>
                    <SelectItem value="replace">Replace</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={handleImport}>Apply import</Button>
                <Button variant="ghost" onClick={downloadTemplate}>Download template</Button>
              </div>
            </div>
            <div className="mt-3">
              <Textarea
                value={importText}
                onChange={(event) => setImportText(event.target.value)}
                placeholder="Paste from Excel: Date<Tab>On duty<Tab>Off duty<Tab>Total commute (min)"
                rows={3}
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Accepts CSV or tab-separated rows with Date, On duty, Off duty, and optional Total commute (min).
            </p>
          </div>
          <div className="overflow-x-auto">
            <Table className={useDefaultsOnly ? "min-w-[2000px]" : "min-w-[2400px]"}>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[70px]">Row</TableHead>
                  <TableHead>Day/Date</TableHead>
                  <TableHead>On duty</TableHead>
                  <TableHead>Off duty</TableHead>
                  <TableHead>Total commute (min)</TableHead>
                  {!useDefaultsOnly && (
                    <>
                      <TableHead>Job/breaks default</TableHead>
                      <TableHead>Workload</TableHead>
                      <TableHead>Attention</TableHead>
                      <TableHead>Break freq</TableHead>
                      <TableHead>Break avg</TableHead>
                      <TableHead>Cont. work</TableHead>
                      <TableHead>Break after</TableHead>
                    </>
                  )}
                  <TableHead>Duty length</TableHead>
                  <TableHead>Rest length</TableHead>
                    <TableHead>Fatigue cumulative</TableHead>
                    <TableHead>Fatigue duty timing</TableHead>
                    <TableHead>Fatigue job/breaks</TableHead>
                    <TableHead>Fatigue Index</TableHead>
                    <TableHead>Risk cumulative</TableHead>
                    <TableHead>Risk duty timing</TableHead>
                    <TableHead>Risk job/breaks</TableHead>
                    <TableHead>Risk Index</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {duties.map((duty, index) => {
                  const commuteValue = duty.commuteMinutes ?? defaults.commuteMinutes;
                  const workloadValue = duty.useDefaultJobBreaks ? defaults.jobWorkload : duty.jobWorkload ?? defaults.jobWorkload;
                  const attentionValue = duty.useDefaultJobBreaks ? defaults.jobAttention : duty.jobAttention ?? defaults.jobAttention;
                  const breakFrequencyValue = duty.useDefaultJobBreaks ? defaults.breakFrequencyMinutes : duty.breakFrequencyMinutes ?? defaults.breakFrequencyMinutes;
                  const breakAverageValue = duty.useDefaultJobBreaks ? defaults.breakAverageLengthMinutes : duty.breakAverageLengthMinutes ?? defaults.breakAverageLengthMinutes;
                  const continuousWorkValue = duty.useDefaultJobBreaks ? defaults.continuousWorkLengthMinutes : duty.continuousWorkLengthMinutes ?? defaults.continuousWorkLengthMinutes;
                  const breakAfterValue = duty.useDefaultJobBreaks ? defaults.breakAfterContinuousMinutes : duty.breakAfterContinuousMinutes ?? defaults.breakAfterContinuousMinutes;
                  const fatigueResult = fatigueCalc.duties[index];
                  const riskResult = riskCalc.duties[index];
                  const dutyLength = fatigueResult ? formatHoursMinutes(fatigueResult.dutyLengthHours) : '-';
                  const restLength = fatigueResult
                    ? (fatigueResult.restLengthHours === null ? 'Fully rested' : formatHoursMinutes(fatigueResult.restLengthHours))
                    : '-';
                  return (
                    <TableRow key={duty.id}>
                      <TableCell className="text-sm text-muted-foreground tabular-nums">{index + 1}</TableCell>
                      <TableCell className="min-w-[160px]">
                        <Input
                          type="date"
                          value={duty.day}
                          className="w-full"
                          onChange={(event) => updateDuty(duty.id, 'day', event.target.value)}
                        />
                      </TableCell>
                      <TableCell className="min-w-[120px]">
                        <Input
                          type="time"
                          value={duty.onDuty}
                          className="w-full"
                          onChange={(event) => updateDuty(duty.id, 'onDuty', event.target.value)}
                        />
                      </TableCell>
                      <TableCell className="min-w-[120px]">
                        <Input
                          type="time"
                          value={duty.offDuty}
                          className="w-full"
                          onChange={(event) => updateDuty(duty.id, 'offDuty', event.target.value)}
                        />
                      </TableCell>
                      <TableCell className="min-w-[120px]">
                        <Input
                          type="number"
                          value={commuteValue}
                          className="w-full"
                          onChange={(event) => updateDuty(duty.id, 'commuteMinutes', parseInt(event.target.value, 10) || 0)}
                        />
                      </TableCell>
                      {!useDefaultsOnly && (
                        <>
                          <TableCell className="text-center">
                            <Switch
                              checked={duty.useDefaultJobBreaks}
                              onCheckedChange={(value) => updateDuty(duty.id, 'useDefaultJobBreaks', value)}
                            />
                          </TableCell>
                          <TableCell className="min-w-[190px]">
                            <Select
                              value={String(workloadValue)}
                              onValueChange={(value) => updateDuty(duty.id, 'jobWorkload', parseInt(value, 10))}
                              disabled={duty.useDefaultJobBreaks}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {workloadOptions.map(option => (
                                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="min-w-[190px]">
                            <Select
                              value={String(attentionValue)}
                              onValueChange={(value) => updateDuty(duty.id, 'jobAttention', parseInt(value, 10))}
                              disabled={duty.useDefaultJobBreaks}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {attentionOptions.map(option => (
                                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="min-w-[120px]">
                            <Input
                              type="number"
                              value={breakFrequencyValue}
                              disabled={duty.useDefaultJobBreaks}
                              className="w-full"
                              onChange={(event) => updateDuty(duty.id, 'breakFrequencyMinutes', parseInt(event.target.value, 10) || 0)}
                            />
                          </TableCell>
                          <TableCell className="min-w-[120px]">
                            <Input
                              type="number"
                              value={breakAverageValue}
                              disabled={duty.useDefaultJobBreaks}
                              className="w-full"
                              onChange={(event) => updateDuty(duty.id, 'breakAverageLengthMinutes', parseInt(event.target.value, 10) || 0)}
                            />
                          </TableCell>
                          <TableCell className="min-w-[120px]">
                            <Input
                              type="number"
                              value={continuousWorkValue}
                              disabled={duty.useDefaultJobBreaks}
                              className="w-full"
                              onChange={(event) => updateDuty(duty.id, 'continuousWorkLengthMinutes', parseInt(event.target.value, 10) || 0)}
                            />
                          </TableCell>
                          <TableCell className="min-w-[120px]">
                            <Input
                              type="number"
                              value={breakAfterValue}
                              disabled={duty.useDefaultJobBreaks}
                              className="w-full"
                              onChange={(event) => updateDuty(duty.id, 'breakAfterContinuousMinutes', parseInt(event.target.value, 10) || 0)}
                            />
                          </TableCell>
                        </>
                      )}
                      <TableCell className="text-sm">{dutyLength}</TableCell>
                      <TableCell className="text-sm">{restLength}</TableCell>
                      <TableCell className="text-sm">{fatigueResult ? fatigueResult.cumulativeComponent.toFixed(2) : '-'}</TableCell>
                      <TableCell className="text-sm">{fatigueResult ? fatigueResult.dutyTimingComponent.toFixed(2) : '-'}</TableCell>
                      <TableCell className="text-sm">{fatigueResult ? fatigueResult.jobBreaksComponent.toFixed(2) : '-'}</TableCell>
                      <TableCell className="text-sm">{fatigueResult ? fatigueResult.index.toFixed(1) : '-'}</TableCell>
                      <TableCell className="text-sm">{riskResult ? riskResult.cumulativeComponent.toFixed(2) : '-'}</TableCell>
                      <TableCell className="text-sm">{riskResult ? riskResult.dutyTimingComponent.toFixed(2) : '-'}</TableCell>
                      <TableCell className="text-sm">{riskResult ? riskResult.jobBreaksComponent.toFixed(2) : '-'}</TableCell>
                      <TableCell className="text-sm">{riskResult ? riskResult.index.toFixed(2) : '-'}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => removeDuty(duty.id)}>
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Policy findings</CardTitle>
          <CardDescription>Fatigue policy checks and Network Rail limits.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {errors.length > 0 && (
            <div className="space-y-1">
              {errors.map(error => (
                <p key={error} className="text-sm text-destructive">{error}</p>
              ))}
            </div>
          )}
          {policyFindings.length === 0 && errors.length === 0 && (
            <p className="text-sm text-muted-foreground">No policy issues detected.</p>
          )}
          {policyFindings.length > 0 && policyCalendar.hasDates && (
            <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Calendar view</p>
                  <p className="text-xs text-muted-foreground">Dates with warnings or breaches.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-destructive" />
                    <span>Breach ({policyCalendar.totalBreaches})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    <span>Warning ({policyCalendar.totalWarnings})</span>
                  </div>
                </div>
              </div>
              <div className="mt-3 rounded-lg border border-border/60 bg-background">
                <Calendar
                  mode="single"
                  selected={selectedPolicyDate}
                  onSelect={handlePolicyDateSelect}
                  numberOfMonths={2}
                  defaultMonth={policyCalendar.defaultMonth}
                  modifiers={{
                    breach: policyCalendar.breachDays,
                    warning: policyCalendar.warningDays,
                  }}
                  modifiersClassNames={{
                    breach: "bg-destructive/20 text-destructive font-semibold",
                    warning: "bg-amber-500/20 text-amber-700 font-semibold",
                  }}
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Click a date to highlight matching findings. Click again to clear.
              </p>
            </div>
          )}
          {policyFindings.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Fatigue</TableHead>
                    <TableHead className="text-right">Risk</TableHead>
                    <TableHead className="text-right">Cumulative hours (7d)</TableHead>
                    <TableHead className="text-right">Cumulative days (14d)</TableHead>
                    <TableHead>Warning</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {policyGroups.map(group => {
                    const isSelectedGroup = selectedPolicyKey && group.label === selectedPolicyKey;
                    return (
                    <Fragment key={group.label}>
                      <TableRow className={isSelectedGroup ? "bg-amber-500/15" : "bg-muted/40"}>
                        <TableCell colSpan={6} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {group.label}
                        </TableCell>
                      </TableRow>
                      {group.items.map(finding => {
                        const row = finding.dutyId ? policyContext.rowsById.get(finding.dutyId) : null;
                        const fatigue = finding.dutyId ? policyContext.fatigueById.get(finding.dutyId) : policyContext.maxFatigue;
                        const risk = finding.dutyId ? policyContext.riskById.get(finding.dutyId) : policyContext.maxRisk;
                        const hours = finding.dutyId ? policyContext.rolling7.get(finding.dutyId) : policyContext.max7;
                        const days = finding.dutyId ? policyContext.rolling14.get(finding.dutyId) : policyContext.max14;
                        const fatigueValue = Number.isFinite(fatigue) ? fatigue.toFixed(1) : '-';
                        const riskValue = Number.isFinite(risk) ? risk.toFixed(2) : '-';
                        const hoursValue = Number.isFinite(hours) ? hours.toFixed(1) : '-';
                        const daysValue = Number.isFinite(days) ? Math.round(days).toString() : '-';
                        const detailLabel = row ? `Row ${row.row} | ${row.time}` : 'All duties';
                        return (
                          <TableRow key={finding.id} className={isSelectedGroup ? "bg-amber-500/10" : undefined}>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="text-sm font-medium">{detailLabel}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{fatigueValue}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{riskValue}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{hoursValue}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{daysValue}</TableCell>
                            <TableCell className="text-sm">
                              <div className="flex flex-col gap-1">
                                <Badge variant={finding.severity === 'breach' ? 'destructive' : 'secondary'}>
                                  {finding.severity === 'breach' ? 'Breach' : 'Warning'}
                                </Badge>
                                <div>
                                  <span className="font-medium">{finding.rule}</span>{' '}
                                  <span className="text-muted-foreground">{finding.message}</span>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </Fragment>
                  );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

ShiftTable.displayName = 'ShiftTable';

export default ShiftTable;
